import * as Phaser from 'phaser';
import { PALETTE } from '../render/palette';

export const ROTATION_STEP_DEG = 15;

export interface MirrorOptions {
  scene: Phaser.Scene;
  cellSize: number;
  gridX: number;
  gridY: number;
  initialAngleDeg?: number;
}

const BAR_LEN = 48;
const BAR_HEIGHT = 5;
const BAR_RADIUS = 2.2;

export class Mirror extends Phaser.GameObjects.Container {
  public angleDeg: number;
  public slotIndex = -1;
  private readonly gfx: Phaser.GameObjects.Graphics;

  constructor(opts: MirrorOptions) {
    super(opts.scene, (opts.gridX + 0.5) * opts.cellSize, (opts.gridY + 0.5) * opts.cellSize);
    this.angleDeg = opts.initialAngleDeg ?? 45;
    this.gfx = opts.scene.add.graphics();
    this.drawBar();
    this.add(this.gfx);
    this.applyRotation();
    opts.scene.add.existing(this);
  }

  rotateBy(deltaDeg: number): void {
    this.setAngleDeg(this.angleDeg + deltaDeg);
  }

  setAngleDeg(deg: number): void {
    this.angleDeg = ((deg % 360) + 360) % 360;
    this.applyRotation();
  }

  private applyRotation(): void {
    this.gfx.setRotation((this.angleDeg * Math.PI) / 180);
  }

  private drawBar(): void {
    const g = this.gfx;
    const halfLen = BAR_LEN / 2;
    const halfH = BAR_HEIGHT / 2;

    // Underside shadow, offset slightly below the bar.
    g.fillStyle(PALETTE.MIRROR_SHADOW, 0.35);
    g.fillRoundedRect(-halfLen, 0.5, BAR_LEN, 3, 1.5);

    // Silver bar — vertical gradient top→bottom approximating the prototype's
    // three-stop fill. WebGL gives us a smooth interpolation; Canvas falls
    // back to the top color, which is still acceptable.
    g.fillGradientStyle(
      PALETTE.MIRROR_TOP,
      PALETTE.MIRROR_TOP,
      PALETTE.MIRROR_BOT,
      PALETTE.MIRROR_BOT,
      1,
    );
    g.fillRoundedRect(-halfLen, -halfH, BAR_LEN, BAR_HEIGHT, BAR_RADIUS);

    // Outline keeps the bar legible against bright backgrounds.
    g.lineStyle(0.5, PALETTE.MIRROR_STROKE, 1);
    g.strokeRoundedRect(-halfLen, -halfH, BAR_LEN, BAR_HEIGHT, BAR_RADIUS);

    // Hairline specular highlight along the top edge.
    g.lineStyle(0.6, PALETTE.MIRROR_HIGHLIGHT, 0.65);
    g.lineBetween(-halfLen + 2, -halfH + 0.9, halfLen - 2, -halfH + 0.9);
  }
}
